/**
 * web/src/lib/planMonth.ts — the month grid behind Life → Plan (card sp7XGAC5XB1H).
 *
 * Read-only by construction: this module only READS the calendar rows the
 * couple already decided on. A plan proposal the couple approved is stored on
 * its meeting as `draft.calendar[]` with `status='added'`, the absolute time
 * the worker pushed to Google (`when`) and Google's own event id — so the
 * month grid is a rendering of rows that already exist, never a second source
 * of truth and never a new write path.
 *
 * Kept out of the .tsx so the grid maths is checkable without a DOM
 * (`scripts/probe-plan-month.mjs`): which day an event lands on, which cells a
 * month has, and which month a set of events belongs to are all pure.
 */

/** The slice of a stored meeting this grid needs — nothing else is read. */
export interface PlanSessionLike {
  sessionId: string;
  /** Meetings filed before card hCF9D2WCpv7Q have no calendar block. */
  draft?: {
    calendar?: {
      id: string;
      text: string;
      status: 'proposed' | 'added' | 'dismissed';
      when: number | null;
    }[];
  } | null;
}

export interface PlanMonthEvent {
  /** The meeting the event came from — tapping the chip opens it. */
  sessionId: string;
  draftId: string;
  /** The event title as the couple worded it. */
  text: string;
  /** Local wall-clock ms the event happens at. */
  at: number;
}

export interface MonthDay {
  /** Local 'YYYY-MM-DD' — the key an event is filed under. */
  key: string;
  /** Day of the month, 1-31. */
  date: number;
  /** False for the tail of the previous month and the head of the next. */
  inMonth: boolean;
  isToday: boolean;
  events: PlanMonthEvent[];
}

export interface MonthGrid {
  year: number;
  /** 0-11, the way Date counts months. */
  month: number;
  /** 'November 2026'. */
  label: string;
  /** Sunday-first weeks, 7 cells each; the first and last may be neighbours. */
  weeks: MonthDay[][];
  /** Every event placed on this month, oldest first. */
  events: PlanMonthEvent[];
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (n: number) => (n < 10 ? '0' + n : String(n));

/** The local calendar day an instant falls on. Local on purpose: the couple
 *  reads the grid in their own timezone, not UTC. */
export function dayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Only decided events can be drawn: `proposed` has no absolute time, and a
 *  dismissed proposal never became an event. A row with a null/missing `when`
 *  is skipped rather than guessed onto a day. */
export function collectMonthEvents(sessions: PlanSessionLike[]): PlanMonthEvent[] {
  const out: PlanMonthEvent[] = [];
  for (const s of sessions) {
    const calendar = s?.draft?.calendar;
    if (!calendar || calendar.length === 0) continue;
    for (const row of calendar) {
      if (row.status !== 'added') continue;
      if (typeof row.when !== 'number' || !Number.isFinite(row.when)) continue;
      out.push({ sessionId: s.sessionId, draftId: row.id, text: row.text, at: row.when });
    }
  }
  return out.sort((a, b) => a.at - b.at);
}

/** The month a grid should open on: today, unless the newest event sits later
 *  (a trip booked for next month is the thing the couple opens Plan to see). */
export function openingMonth(
  events: PlanMonthEvent[],
  now: number = Date.now(),
): { year: number; month: number } {
  const today = new Date(now);
  let year = today.getFullYear();
  let month = today.getMonth();
  const newest = events.length > 0 ? events[events.length - 1].at : null;
  if (newest !== null) {
    const d = new Date(newest);
    if (d.getFullYear() > year || (d.getFullYear() === year && d.getMonth() > month)) {
      year = d.getFullYear();
      month = d.getMonth();
    }
  }
  return { year, month };
}

/** One month forward or back, carrying the year. */
export function stepMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const d = new Date(year, month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

/** The grid: whole weeks, Sunday first, every event filed under its local day. */
export function buildMonthGrid(
  year: number,
  month: number,
  events: PlanMonthEvent[],
  now: number = Date.now(),
): MonthGrid {
  const byDay = new Map<string, PlanMonthEvent[]>();
  for (const e of events) {
    const key = dayKey(e.at);
    const bucket = byDay.get(key);
    if (bucket) bucket.push(e);
    else byDay.set(key, [e]);
  }

  const first = new Date(year, month, 1);
  const leading = first.getDay(); // 0 = Sunday, so the grid needs no shift
  const start = new Date(year, month, 1 - leading);
  const todayKey = dayKey(now);
  const total = new Date(year, month + 1, 0).getDate();
  const cells = Math.ceil((leading + total) / 7) * 7;

  const weeks: MonthDay[][] = [];
  const placed: PlanMonthEvent[] = [];
  for (let i = 0; i < cells; i += 1) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const inMonth = d.getMonth() === month && d.getFullYear() === year;
    const dayEvents = inMonth ? byDay.get(key) ?? [] : [];
    if (inMonth) placed.push(...dayEvents);
    const day: MonthDay = {
      key,
      date: d.getDate(),
      inMonth,
      isToday: key === todayKey,
      events: dayEvents,
    };
    if (i % 7 === 0) weeks.push([day]);
    else weeks[weeks.length - 1].push(day);
  }

  return {
    year,
    month,
    label: `${MONTHS[month]} ${year}`,
    weeks,
    events: placed,
  };
}
